import { useI18n } from "@wojo/localization";
import { LoadingSkeleton, Text } from "@wojo/ui";
import cn from "clsx";
import React, { Suspense } from "react";
import { useTourCodes } from "../../_client/context";
import style from "./TourHeroSummaryInfo.module.scss";
import {
    GetClassLevelContentsByTourCodesDocument,
    GetClassLevelContentsByTourCodesQuery,
    GetClassLevelContentsByTourCodesQueryVariables,
    TourClassLevelContent,
} from "../../../generated/graphql";
import { BusinessCode } from "@wojo/auth/generated/graphql";
import dayjs from "dayjs";
import { TourHeroClassLevelItem } from "./TourHeroClassLevelItem";
import { ErrorBoundary } from "react-error-boundary";
import { useSuspenseQuery } from "@apollo/client";

export const TourHeroClassLevelContent: React.FC = () => {
    const tourCodes = useTourCodes();

    const { data } = useSuspenseQuery<
        GetClassLevelContentsByTourCodesQuery,
        GetClassLevelContentsByTourCodesQueryVariables
    >(GetClassLevelContentsByTourCodesDocument, {
        variables: {
            language: process.env.NEXT_PUBLIC_STORYBLOK_LANGUAGE ?? "",
            filter: {
                tourCodes: tourCodes,
                businessCode: process.env
                    .NEXT_PUBLIC_BUSINESS_CODE as BusinessCode,
            },
        },
    });

    const classLevelData = data.tours.results
        .filter((tour) => tour.priceYear.year >= dayjs().utc().year())
        .reduce((acc, tour) => {
            if (tour.classLevelContent?.name) {
                acc[tour.classLevelContent.name] = tour.classLevelContent;
            }
            return acc;
        }, {} as Record<string, TourClassLevelContent>);

    const classLevelValues = Object.values(classLevelData);

    if (classLevelValues.length === 0) {
        return null;
    }

    return (
        <Text.Body tag="dd" style={{ display: "flex", justifyContent: "end" }}>
            {classLevelValues.map((classLevel) => {
                return (
                    <TourHeroClassLevelItem
                        key={classLevel.name}
                        classLevel={classLevel}
                    />
                );
            })}
        </Text.Body>
    );
};

export const TourHeroClassLevel: React.FC = () => {
    const { productPage } = useI18n("productPage");

    return (
        <Text
            tag="dl"
            className={cn(
                style["summary-info-detail-wrapper"],
                style["summary-info-detail-wrapper--class-level"],
            )}
        >
            <Text tag="dt">
                {productPage.tourHero.heroSummary.classLevel.l()}
            </Text>
            <ErrorBoundary fallback={null}>
                <Suspense
                    fallback={
                        <LoadingSkeleton
                            accessibleText={productPage.tourHero.heroSummary.loadingClassLevel.l()}
                            height="1lh"
                            width="100px"
                            style={{ justifySelf: "end" }}
                        />
                    }
                >
                    <TourHeroClassLevelContent />
                </Suspense>
            </ErrorBoundary>
        </Text>
    );
};
